import { isSiteHeaderAnimating } from '../../hooks/useNavigationTopOffset';

import { NAVIGATION_ROOT_ID } from './constants';

const SETTLE_MS = 120;
const MAX_SCROLL_MS = 3000;
// A header can animate forever (a looping reveal, a scroll-driven effect) and
// keep the measured offset moving with it. Wait for it only briefly, or the
// correction below never runs at all.
const ANIMATION_GRACE_MS = 1000;

export function scrollToElementWithNavbarOffset(
  element: Element | null,
  measureTopOffset: () => number,
  onComplete: () => void,
): () => void {
  const navbar = document.getElementById(NAVIGATION_ROOT_ID);

  if (!element || !navbar) {
    onComplete();

    return () => {};
  }

  let frame: number;
  let stopped = false;
  const start = performance.now();
  let lastChange = start;
  let previousY = window.scrollY;
  let previousOffset = measureTopOffset();
  const interruptEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

  function stop() {
    if (stopped) {
      return;
    }
    stopped = true;
    cancelAnimationFrame(frame);
    interruptEvents.forEach((event) => window.removeEventListener(event, stop));
    onComplete();
  }

  function targetY(offset: number) {
    const target =
      element!.getBoundingClientRect().top +
      window.scrollY -
      navbar!.getBoundingClientRect().height -
      offset;
    const maxScroll = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight,
    );

    return Math.max(0, Math.min(maxScroll, target));
  }

  function settle(now: number) {
    if (
      !element!.isConnected ||
      !navbar!.isConnected ||
      now - start > MAX_SCROLL_MS
    ) {
      stop();

      return;
    }
    const offset = measureTopOffset();
    // Inside the grace period anything still in flight counts as unsettled: the
    // native smooth scroll, a header animation, or the offset one is changing.
    // After it, only the scroll position does, so a header whose measured
    // bottom never stops moving can no longer hold the correction off until the
    // cutoff. The correction always re-reads the offset, so acting while the
    // header is still moving still lands on its current position.
    const moving =
      now - start < ANIMATION_GRACE_MS
        ? window.scrollY !== previousY ||
          offset !== previousOffset ||
          isSiteHeaderAnimating()
        : window.scrollY !== previousY;

    if (moving) {
      lastChange = now;
      previousY = window.scrollY;
      previousOffset = offset;
    } else if (now - lastChange >= SETTLE_MS) {
      const top = targetY(offset);

      if (Math.abs(top - window.scrollY) <= 1) {
        stop();

        return;
      }
      // A header may appear/disappear during the native smooth scroll. Correct
      // only after it settles, using the same measurement as the sticky top.
      window.scrollTo({ top, behavior: 'auto' });
      lastChange = now;
    }
    frame = requestAnimationFrame(settle);
  }

  interruptEvents.forEach((event) =>
    window.addEventListener(event, stop, { passive: true }),
  );
  window.scrollTo({ top: targetY(previousOffset), behavior: 'smooth' });
  frame = requestAnimationFrame(settle);

  return stop;
}
